import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faPencil,
  faTrash,
  faDollarSign,
  faCube,
} from '@fortawesome/free-solid-svg-icons';
import api from '../services/api';

const MaterialsMasterManagement = () => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [formData, setFormData] = useState({
    material_type: '',
    material_name: '',
    cost_per_unit_cad: '',
    cost_per_gal_cdn: '',
    thickness_mils: '',
    coverage_ft2_per_gal: '',
    coverage_field_mils: '',
    cost_per_unit_cdn: '',
    cost_per_m2_cdn: '',
    year: 2025,
    currency: 'CAD',
    is_active: true,
    });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [materialTypeFilter, setMaterialTypeFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  // Fetch materials
  const fetchMaterials = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...(searchTerm && { search: searchTerm }),
        ...(materialTypeFilter && { material_type: materialTypeFilter }),
        ...(yearFilter && { year: yearFilter }),
        
      });

      const response = await api.get(`/materials-master?${params}`);
      if (response.data.success) {
        setMaterials(response.data.data);
        setPagination(response.data.pagination);
      }
    } catch (err) {
      setError('Failed to fetch materials master');
      console.error('Error fetching materials:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMaterials();
  }, [pagination.page, searchTerm, materialTypeFilter, yearFilter]);

  // Check for duplicate active material
  const checkDuplicate = async (materialType, year, isActive, excludeId = null) => {
    if (!materialType || !year || !isActive) {
      setDuplicateWarning(null);
      return;
    }

    try {
      const params = new URLSearchParams({
        material_type: materialType,
        year: year,
        is_active: 'true'
      });
      const response = await api.get(`/materials-master?${params}`);
      
      if (response.data.success && response.data.data.length > 0) {
        // If editing, exclude current material from check
        const duplicate = excludeId 
          ? response.data.data.find(m => m.id !== excludeId)
          : response.data.data[0];
        
        if (duplicate) {
          setDuplicateWarning(`⚠️ An active ${materialType} material for year ${year} already exists: "${duplicate.materialName}". Only one active material per type per year is allowed.`);
        } else {
          setDuplicateWarning(null);
        }
      } else {
        setDuplicateWarning(null);
      }
    } catch (err) {
      // Silently fail - backend will catch it anyway
      setDuplicateWarning(null);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check for duplicate before submission
    if (duplicateWarning && formData.is_active) {
      const proceed = window.confirm(
        `${duplicateWarning}\n\nDo you want to proceed anyway?`
      );
      if (!proceed) {
        return;
      }
    }

    try {
      if (editingMaterial) {
        await api.put(`/materials-master/${editingMaterial.id}`, formData);
      } else {
        await api.post('/materials-master', formData);
      }
      
      setShowModal(false);
      setEditingMaterial(null);
      setDuplicateWarning(null);
      setError(null);
      setFormData({
        material_type: '',
        material_name: '',
        cost_per_unit_cad: '',
        cost_per_gal_cdn: '',
        thickness_mils: '',
        coverage_ft2_per_gal: '',
        coverage_field_mils: '',
        cost_per_unit_cdn: '',
        cost_per_m2_cdn: '',
        year: 2025,
        currency: 'CAD',
        });
      fetchMaterials();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to save material master';
      setError(errorMessage);
      console.error('Error saving material:', err);
    }
  };

  // Handle edit
  const handleEdit = (material) => {
    setEditingMaterial(material);
    setFormData({
      material_type: material.materialType,
      material_name: material.materialName,
      cost_per_unit_cad: material.costPerUnitCad,
      cost_per_gal_cdn: material.costPerGalCdn || '',
      thickness_mils: material.thicknessMils || '',
      coverage_ft2_per_gal: material.coverageFt2PerGal || '',
      coverage_field_mils: material.coverageFieldMils || '',
      cost_per_unit_cdn: material.costPerUnitCdn || '',
      cost_per_m2_cdn: material.costPerM2Cdn || '',
      year: material.year,
      currency: material.currency,
      is_active: material.isActive
    });
    setDuplicateWarning(null);
    // Check for duplicates when editing
    if (material.isActive) {
      checkDuplicate(material.materialType, material.year, material.isActive, material.id);
    }
    setShowModal(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this material master?')) {
      try {
        await api.delete(`/materials-master/${id}`);
        fetchMaterials();
      } catch (err) {
        setError('Failed to delete material master');
        console.error('Error deleting material:', err);
      }
    }
  };

  

  // Reset form
  const resetForm = () => {
    setFormData({
      material_type: '',
      material_name: '',
      cost_per_unit_cad: '',
      cost_per_gal_cdn: '',
      thickness_mils: '',
      coverage_ft2_per_gal: '',
      coverage_field_mils: '',
      cost_per_unit_cdn: '',
      cost_per_m2_cdn: '',
      year: 2025,
      currency: 'CAD',
      });
    setEditingMaterial(null);
  };

  // Get material type color
  const getMaterialTypeColor = (type) => {
    const colors = {
      prime: 'bg-blue-100 text-blue-800',
      stripe: 'bg-purple-100 text-purple-800',
      midcoat: 'bg-green-100 text-green-800',
      topcoat: 'bg-orange-100 text-orange-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Materials Master Management</h1>
        <p className="text-gray-600">Manage coating materials with detailed specifications and costs</p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Filters and Search */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search materials..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={materialTypeFilter}
          onChange={(e) => setMaterialTypeFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Types</option>
          <option value="prime">Prime</option>
          <option value="stripe">Stripe</option>
          <option value="midcoat">Midcoat</option>
          <option value="topcoat">Topcoat</option>
        </select>
        <select
          value={yearFilter}
          onChange={(e) => setYearFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Years</option>
          <option value="2024">2024</option>
          <option value="2025">2025</option>
        </select>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2"
        >
          <FontAwesomeIcon icon={faPlus} className="h-4 w-4" />
          Add Material
        </button>
      </div>

      {/* Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading materials...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Material Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cost per Unit (CAD)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cost per m² CDN
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Thickness
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Coverage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Year
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {materials.map((material) => (
                  <tr key={material.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getMaterialTypeColor(material.materialType)}`}>
                        {material.materialType.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center">
                        <FontAwesomeIcon icon={faCube} className="h-4 w-4 text-gray-400 mr-2" />
                        {material.materialName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center">
                        <FontAwesomeIcon icon={faDollarSign} className="h-4 w-4 text-green-600 mr-1" />
                        <span className="font-medium">{material.costPerUnitCad != null ? material.costPerUnitCad : '-'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center">
                        <FontAwesomeIcon icon={faDollarSign} className="h-4 w-4 text-blue-600 mr-1" />
                        <span>{material.costPerM2Cdn != null ? material.costPerM2Cdn : '-'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {material.thicknessMils ? `${material.thicknessMils} mils` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {material.coverageFt2PerGal ? `${material.coverageFt2PerGal} ft²/gal` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {material.year}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(material)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit"
                        >
                          <FontAwesomeIcon icon={faPencil} className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(material.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <FontAwesomeIcon icon={faTrash} className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                disabled={pagination.page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                disabled={pagination.page === pagination.pages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{' '}
                  <span className="font-medium">
                    {(pagination.page - 1) * pagination.limit + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium">
                    {Math.min(pagination.page * pagination.limit, pagination.total)}
                  </span>{' '}
                  of <span className="font-medium">{pagination.total}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                    disabled={pagination.page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setPagination({ ...pagination, page })}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        page === pagination.page
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                    disabled={pagination.page === pagination.pages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingMaterial ? 'Edit Material Master' : 'Add New Material Master'}
              </h3>
              {duplicateWarning && (
                <div className="mb-4 bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded">
                  {duplicateWarning}
                </div>
              )}
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Material Type *
                    </label>
                    <select
                      required
                      value={formData.material_type}
                      onChange={(e) => {
                        const newType = e.target.value;
                        setFormData({ ...formData, material_type: newType });
                        checkDuplicate(newType, formData.year, formData.is_active, editingMaterial?.id);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Material Type</option>
                      <option value="prime">Prime</option>
                      <option value="stripe">Stripe</option>
                      <option value="midcoat">Midcoat</option>
                      <option value="topcoat">Topcoat</option>
                    </select>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Material Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.material_name}
                      onChange={(e) => setFormData({ ...formData, material_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Corothane I Galvapac"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cost per Unit (CAD) *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={formData.cost_per_unit_cad}
                      onChange={(e) => setFormData({ ...formData, cost_per_unit_cad: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                    />
                    <p className="mt-1 text-xs text-gray-500">Used as fallback in simulation when Cost per m² CDN is not set.</p>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cost per m² CDN
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.cost_per_m2_cdn}
                      onChange={(e) => setFormData({ ...formData, cost_per_m2_cdn: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                    />
                    <p className="mt-1 text-xs text-gray-500">Primary cost used in simulation and repair projects.</p>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cost per Gallon (CAD)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.cost_per_gal_cdn}
                      onChange={(e) => setFormData({ ...formData, cost_per_gal_cdn: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Thickness (mils)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.thickness_mils}
                      onChange={(e) => setFormData({ ...formData, thickness_mils: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Coverage (Ft² per Gallon)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.coverage_ft2_per_gal}
                      onChange={(e) => setFormData({ ...formData, coverage_ft2_per_gal: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Coverage Field (mils)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.coverage_field_mils}
                      onChange={(e) => setFormData({ ...formData, coverage_field_mils: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Year *
                    </label>
                    <input
                      type="number"
                      required
                      value={formData.year}
                      onChange={(e) => {
                        const newYear = parseInt(e.target.value) || 2025;
                        setFormData({ ...formData, year: newYear });
                        checkDuplicate(formData.material_type, newYear, formData.is_active, editingMaterial?.id);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="2025"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Currency *
                    </label>
                    <select
                      required
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="USD">USD</option>
                      <option value="CAD">CAD</option>
                    </select>
                  </div>
                  <div className="mb-4 md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Active Status
                    </label>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_active !== undefined ? formData.is_active : true}
                        onChange={(e) => {
                          const newIsActive = e.target.checked;
                          setFormData({ ...formData, is_active: newIsActive });
                          checkDuplicate(formData.material_type, formData.year, newIsActive, editingMaterial?.id);
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">
                        This material is active
                      </label>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {editingMaterial ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialsMasterManagement;
